import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, InfoIcon, XIcon } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';

import { projectMembersQuery } from '@/entities/project-member';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useApprovalChainDetail, type UpdateApprovalChainInput } from '@/entities/approval-chain';
import { EmptyState } from '@/shared/ui/EmptyState';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { useCreateApprovalChain } from '../../api/useCreateApprovalChain.mutation';
import { useUpdateApprovalChain } from '../../api/useUpdateApprovalChain.mutation';
import { readAutoApplyConflict } from '../../lib/auto-apply-conflict';
import {
  mapApprovalChainToFormData,
  type ApprovalChainAssigneeOption,
  type ApprovalChainFormInput,
} from '../../model/schema';
import { ApprovalChainForm } from '../ApprovalChainForm';
import { EditApprovalChainWarningDialog } from '../EditApprovalChainWarningDialog';
import { useStepRoleCatalogue } from './useStepRoleCatalogue';
import { useTranslation } from 'react-i18next';

type ApprovalChainDto = UpdateApprovalChainInput;

export interface ApprovalChainEditorTarget {
  /** Omitted for a create flow. */
  approvalChainId?: string;
  /** Copy-from-another-project prefill, handed over in memory by the picker. */
  prefill?: ApprovalChainFormInput;
  prefillSourceName?: string;
}

export interface ApprovalChainEditorPanelProps {
  target: ApprovalChainEditorTarget;
  projectId: string;
  /** Shuts the whole drawer. */
  onClose: () => void;
  /** Returns to the list view, which every editor is reachable from. */
  onBack: () => void;
  onSaved: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * Approval chain creator/editor, as the drawer's second subview so the list,
 * folder or asset the manager came from stays behind it.
 *
 * Saving a chain that assets are already running re-confirms via
 * `EditApprovalChainWarningDialog`, because the edit changes in-progress
 * approvals. The other guard — losing edits by leaving — belongs to the drawer,
 * which owns every way out of here.
 *
 * The host must render this keyed on the drawer's `renderKey`; see the controller.
 */
export function ApprovalChainEditorPanel({
  target,
  projectId,
  onClose,
  onBack,
  onSaved,
  onDirtyChange,
}: ApprovalChainEditorPanelProps) {
  const { t } = useTranslation('approvalChains');

  const approvalChainId = target.approvalChainId;
  const isEdit = Boolean(approvalChainId);

  const workspaceId = useCurrentWorkspaceId();

  const {
    data: approvalChain,
    isLoading: isLoadingApprovalChain,
    isError: isApprovalChainError,
  } = useApprovalChainDetail(projectId, approvalChainId);

  // Project members power the assignee picker's initial list in every step.
  // When the workspace-assignment flag is on, each step's picker searches the
  // broader workspace roster server-side on demand rather than this panel
  // eagerly loading the whole roster.
  const { data: membersResponse, isLoading: isLoadingMembers } = useQuery(
    projectMembersQuery(workspaceId, projectId),
  );

  const members = useMemo<ApprovalChainAssigneeOption[]>(
    () =>
      (membersResponse?.data ?? []).map((member) => ({
        userId: member.id,
        name: member.name,
        email: member.email,
        avatarUrl: member.avatarUrl,
      })),
    [membersResponse],
  );

  const stepRoles = useStepRoleCatalogue(projectId);

  const initialValues = useMemo(
    () => (approvalChain ? mapApprovalChainToFormData(approvalChain) : target.prefill),
    [approvalChain, target.prefill],
  );

  const createApprovalChain = useCreateApprovalChain({ projectId });
  const updateApprovalChain = useUpdateApprovalChain({ projectId });
  const isSubmitting = createApprovalChain.isPending || updateApprovalChain.isPending;

  // Holds the mapped DTO while the "active on X assets" confirm is open.
  const [pendingDto, setPendingDto] = useState<ApprovalChainDto | null>(null);
  const [autoApplyConflictMessage, setAutoApplyConflictMessage] = useState<string | null>(null);

  // The auto-apply conflict belongs to a field the user can fix, so it goes under that field
  // rather than in a toast that names no field and disappears on its own.
  const showSaveError = (error: unknown, fallback: string) => {
    const conflictingApprovalChainName = readAutoApplyConflict(error);
    if (conflictingApprovalChainName !== undefined) {
      setAutoApplyConflictMessage(
        t('manageApprovalChains.approvalChainForm.autoApplyOnAnotherChain', {
          approvalChainName: conflictingApprovalChainName,
        }),
      );
      return;
    }
    notificationService.showError(parseProblemDetail(error, fallback).detail);
  };

  const submitCreate = (dto: ApprovalChainDto) => {
    createApprovalChain.mutate(dto, {
      onSuccess: (created) => {
        notificationService.showSuccess(
          t('manageApprovalChains.approvalChainEditorPanel.created', {
            approvalChainName: created.name,
          }),
        );
        onSaved();
      },
      onError: (error) => {
        showSaveError(
          error,
          t('manageApprovalChains.approvalChainEditorPanel.failedToCreateApprovalChain'),
        );
      },
    });
  };

  const submitUpdate = (dto: ApprovalChainDto) => {
    if (!approvalChainId) return;
    updateApprovalChain.mutate(
      { approvalChainId, body: dto },
      {
        onSuccess: (updated) => {
          notificationService.showSuccess(
            t('manageApprovalChains.approvalChainEditorPanel.savedChangesTo', {
              approvalChainName: updated.name,
            }),
          );
          onSaved();
        },
        onError: (error) => {
          showSaveError(
            error,
            t('manageApprovalChains.approvalChainEditorPanel.failedToSaveApprovalChain'),
          );
        },
      },
    );
  };

  const handleSubmit = (dto: ApprovalChainDto) => {
    setAutoApplyConflictMessage(null);
    if (!isEdit) {
      submitCreate(dto);
      return;
    }
    // Editing a chain that's live on assets changes in-progress approvals, so
    // confirm before the mutation rather than after.
    if (approvalChain && approvalChain.assetUsageCount > 0) {
      setPendingDto(dto);
      return;
    }
    submitUpdate(dto);
  };

  const handleConfirmSave = () => {
    if (!pendingDto) return;
    const dto = pendingDto;
    setPendingDto(null);
    submitUpdate(dto);
  };

  const showLoading = isEdit && isLoadingApprovalChain;
  const showError = isEdit && !isLoadingApprovalChain && (isApprovalChainError || !approvalChain);

  return (
    <TooltipProvider>
      <div
        className="flex h-full min-h-0 flex-col gap-md"
        data-testid="approval-chain-editor-panel"
      >
        <div className="flex shrink-0 flex-col gap-xs">
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="mini"
              onClick={onBack}
              disabled={isSubmitting}
              aria-label={t('manageApprovalChains.approvalChainEditorPanel.backToApprovalChains')}
              data-testid="approval-chain-editor-panel-back"
            >
              <ArrowLeftIcon className="size-4" />
              {t('manageApprovalChains.approvalChainEditorPanel.allApprovalChains')}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="mini"
                  className="size-8 shrink-0 p-0"
                  onClick={onClose}
                  disabled={isSubmitting}
                  aria-label={t('manageApprovalChains.approvalChainEditorPanel.closePanel')}
                  data-testid="approval-chain-editor-panel-close"
                >
                  <XIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('manageApprovalChains.approvalChainEditorPanel.closePanel')}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-medium">
              {isEdit
                ? t('manageApprovalChains.approvalChainEditorPanel.editApprovalChain')
                : t('manageApprovalChains.approvalChainListPanel.createNewApprovalChain')}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {t('manageApprovalChains.approvalChainEditorPanel.configureAnApprovalChainFor')}
            </p>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {showLoading ? (
            <div className="flex justify-center py-5xl" data-testid="approval-chain-editor-loading">
              <Spinner />
            </div>
          ) : showError ? (
            <EmptyState
              title={t('manageApprovalChains.approvalChainEditorPanel.approvalChainNotFound')}
              description={t(
                'manageApprovalChains.approvalChainEditorPanel.thisApprovalChainMayHaveBeen',
              )}
              data-testid="approval-chain-editor-error-state"
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-md">
              {target.prefillSourceName != null && (
                <Alert icon={<InfoIcon />} data-testid="approval-chain-editor-copied-banner">
                  <AlertTitle>
                    {t('manageApprovalChains.approvalChainEditorPanel.copiedFrom', {
                      approvalChainName: target.prefillSourceName,
                    })}
                  </AlertTitle>
                  <AlertDescription>
                    {t('manageApprovalChains.approvalChainEditorPanel.approversWerenTCopiedAdd')}
                  </AlertDescription>
                </Alert>
              )}
              <ApprovalChainForm
                initialValues={initialValues}
                members={members}
                isLoadingMembers={isLoadingMembers}
                workspaceId={workspaceId}
                stepRoles={stepRoles}
                isSubmitting={isSubmitting}
                submitLabel={
                  isEdit
                    ? t('manageApprovalChains.approvalChainEditorPanel.saveChanges')
                    : t('manageApprovalChains.approvalChainEditorPanel.createApprovalChain')
                }
                onSubmit={handleSubmit}
                onCancel={onBack}
                onDirtyChange={onDirtyChange}
                autoApplyConflictMessage={autoApplyConflictMessage}
              />
            </div>
          )}
        </div>

        <EditApprovalChainWarningDialog
          open={!!pendingDto}
          affectedAssetCount={approvalChain?.assetUsageCount ?? 0}
          changesExecutionMode={
            !!pendingDto?.executionMode &&
            !!approvalChain &&
            pendingDto.executionMode !== approvalChain.executionMode
          }
          onCancel={() => setPendingDto(null)}
          onProceed={handleConfirmSave}
        />
      </div>
    </TooltipProvider>
  );
}
